import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Loader2, CheckCircle2, AlertTriangle, Clock, Calendar, Store, Bike, Tag, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { useAuthStore } from '@/lib/auth';
import { eur } from '@/lib/format';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/Header';
import { BaseMap, Marker, Polygon, RESTAURANT_ICON, CUSTOMER_ICON, Recenter } from '@/components/MapBase';
import { useScheduleStatus } from '@/lib/schedule';

export default function CheckoutPage() {
  const items = useCart(s => s.items);
  const subtotal = useCart(s => s.subtotal());
  const clear = useCart(s => s.clear);
  const { user } = useAuthStore();
  const nav = useNavigate();
  const schedule = useScheduleStatus();

  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState(user?.email || '');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [payment, setPayment] = useState('cash');
  const [scheduledFor, setScheduledFor] = useState('');  // '' = ASAP
  const [orderType, setOrderType] = useState('delivery'); // 'delivery' | 'takeaway'

  // Discount code
  const [discountInput, setDiscountInput] = useState('');
  const [discount, setDiscount] = useState(null); // { code, type, value, amount }
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState('');

  useEffect(() => {
    api.get('/zones').then(r => setZones(r.data));
    api.get('/settings').then(r => setSettings(r.data));
    if (user?.role === 'customer') {
      api.get('/me/addresses').then(r => {
        setSavedAddresses(r.data);
        // Auto-select default address
        const def = r.data.find(a => a.is_default);
        if (def) {
          setSelectedAddrId(def.id);
          setAddress(def.address);
          setQuote({ lat: def.lat, lng: def.lng, display_name: def.address, deliverable: true, delivery_fee: null, zone_name: null });
          // Fetch fresh quote to get zone/fee
          api.post('/quote', { lat: def.lat, lng: def.lng }).then(qr => setQuote(qr.data)).catch(() => {});
          if (def.notes) setNotes(def.notes);
        }
      }).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    if (items.length === 0) {
      toast.info('Il carrello è vuoto');
      nav('/menu');
    }
  }, [items.length, nav]);

  // Compute available future delivery slots from opening hours (next 7 days)
  const availableSlots = useMemo(() => {
    if (!schedule?.hours) return [];
    const now = new Date();
    const list = [];
    const dayLabels = ['Domenica','Lunedì','Martedì','Mercoledì','Giovedì','Venerdì','Sabato'];
    for (let offset = 0; offset < 7; offset++) {
      const d = new Date(now); d.setDate(d.getDate() + offset);
      // Convert to weekday: JS getDay 0=Sun ; backend day 0=Mon ; convert
      const jsDay = d.getDay();
      const backendDay = (jsDay + 6) % 7;
      const dayCfg = schedule.hours.find(h => h.day === backendDay);
      if (!dayCfg?.open) continue;
      for (const slot of dayCfg.slots || []) {
        const [fh, fm] = (slot.from || '00:00').split(':').map(Number);
        const [th, tm] = (slot.to || '00:00').split(':').map(Number);
        // generate 15-min intervals with 15 min lead time from now
        const start = new Date(d); start.setHours(fh, fm, 0, 0);
        const end = new Date(d); end.setHours(th, tm, 0, 0);
        const min = new Date(now.getTime() + 15 * 60000);
        const t = new Date(Math.max(start.getTime(), Math.ceil(min.getTime() / (15*60000)) * (15*60000)));
        // round to nearest 15 min upward
        const iter = new Date(t);
        while (iter <= end) {
          if (iter >= start) {
            const iso = iter.toISOString();
            const label = `${offset === 0 ? 'Oggi' : offset === 1 ? 'Domani' : dayLabels[iter.getDay()]} ${String(iter.getDate()).padStart(2,'0')}/${String(iter.getMonth()+1).padStart(2,'0')} — ${String(iter.getHours()).padStart(2,'0')}:${String(iter.getMinutes()).padStart(2,'0')}`;
            list.push({ iso, label });
          }
          iter.setMinutes(iter.getMinutes() + 15);
        }
      }
    }
    return list.slice(0, 60);
  }, [schedule]);

  const isOpen = schedule?.is_open;
  // If closed, force user to pick a scheduled slot
  const needsSchedule = !isOpen && schedule;

  const deliveryFee = useMemo(() => {
    if (orderType === 'takeaway') return 0;
    if (!quote?.deliverable) return 0;
    return quote.delivery_fee || 0;
  }, [orderType, quote]);

  const discountAmount = discount?.amount || 0;

  const total = useMemo(() => {
    const t = subtotal + deliveryFee - discountAmount;
    return t < 0 ? 0 : t;
  }, [subtotal, deliveryFee, discountAmount]);

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) return;
    setDiscountLoading(true); setDiscountError('');
    try {
      const res = await api.post('/discount-codes/validate', { code, subtotal });
      setDiscount({ ...res.data, code: res.data.code || code.toUpperCase() });
      toast.success(`Codice "${res.data.code || code.toUpperCase()}" applicato!`);
    } catch (e) {
      setDiscount(null);
      setDiscountError(e.response?.data?.detail || 'Codice non valido');
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountInput('');
    setDiscountError('');
  };

  const searchAddress = async () => {
    if (!address.trim()) return;
    setQuoteLoading(true); setQuoteError(''); setQuote(null);
    try {
      const res = await api.post('/quote', { address });
      setQuote(res.data);
      if (!res.data.deliverable) setQuoteError('Indirizzo fuori area di consegna');
    } catch (e) {
      setQuoteError(e.response?.data?.detail || 'Errore nella ricerca indirizzo');
    } finally { setQuoteLoading(false); }
  };

  const placeOrder = async () => {
    if (!name || !phone) return toast.error('Inserisci nome e telefono');
    if (!email || !email.trim()) return toast.error('Inserisci l\'email per ricevere la conferma dell\'ordine');
    // Simple email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error('Email non valida');
    if (orderType === 'delivery' && !quote?.deliverable) return toast.error('Verifica l\'indirizzo di consegna');
    if (needsSchedule && !scheduledFor) return toast.error('Siamo chiusi: scegli uno slot di pre-ordine');
    setSubmitting(true);
    try {
      const path = user?.role === 'customer' ? '/orders/authed' : '/orders';
      const payload = {
        customer_name: name,
        customer_phone: phone,
        customer_email: email.trim(),
        items,
        order_type: orderType,
        delivery_notes: notes,
        payment_method: payment,
        scheduled_for: scheduledFor || null,
        discount_code: discount?.code || null,
      };
      if (orderType === 'delivery') {
        payload.delivery_address = quote.display_name || address;
        payload.delivery_lat = quote.lat;
        payload.delivery_lng = quote.lng;
      }
      const res = await api.post(path, payload);
      clear();
      toast.success(scheduledFor ? 'Pre-ordine confermato!' : (orderType === 'takeaway' ? 'Ordine di ritiro confermato!' : 'Ordine inviato!'));
      nav(`/tracking/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio ordine');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 w-full">
        <h1 className="font-display text-3xl font-bold mb-6">Checkout</h1>
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-5">
            <Section title="Modalità di ordine">
              {(() => {
                const deliveryOn = settings?.delivery_enabled !== false;
                const takeawayOn = settings?.takeaway_enabled !== false;
                // Auto-switch to the enabled channel if the current one is off.
                if (orderType === 'delivery' && !deliveryOn && takeawayOn) setTimeout(() => setOrderType('takeaway'), 0);
                if (orderType === 'takeaway' && !takeawayOn && deliveryOn) setTimeout(() => setOrderType('delivery'), 0);
                if (!deliveryOn && !takeawayOn) {
                  return (
                    <div className="rounded-xl bg-[#FDECEC] border border-[--brand-danger]/30 text-[--brand-danger] p-4 text-sm" data-testid="checkout-all-closed">
                      Al momento non stiamo accettando nuovi ordini. Riprova più tardi o contattaci al {settings?.phone || 'telefono'}.
                    </div>
                  );
                }
                return null;
              })()}
              <Tabs value={orderType} onValueChange={setOrderType} data-testid="checkout-order-type-tabs">
                <TabsList className="grid grid-cols-2 w-full h-auto bg-[--brand-surface-2] p-1 rounded-xl">
                  <TabsTrigger
                    value="delivery"
                    disabled={settings?.delivery_enabled === false}
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-2.5 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
                    data-testid="checkout-tab-delivery"
                  >
                    <Bike size={16} className="mr-2" />
                    Consegna a domicilio
                    {settings?.delivery_enabled === false && <span className="ml-2 text-[10px] font-semibold text-[--brand-danger]">Non disponibile</span>}
                  </TabsTrigger>
                  <TabsTrigger
                    value="takeaway"
                    disabled={settings?.takeaway_enabled === false}
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm py-2.5 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed"
                    data-testid="checkout-tab-takeaway"
                  >
                    <Store size={16} className="mr-2" />
                    Ritiro in negozio
                    {settings?.takeaway_enabled === false && <span className="ml-2 text-[10px] font-semibold text-[--brand-danger]">Non disponibile</span>}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="delivery" className="mt-3 text-xs text-[--brand-muted]">
                  Consegna al tuo indirizzo. Il costo dipende dalla zona.
                </TabsContent>
                <TabsContent value="takeaway" className="mt-3">
                  <div className="rounded-xl bg-[#F3FBF7] border border-[--brand-primary]/30 px-4 py-3 flex items-start gap-3" data-testid="checkout-pickup-info">
                    <Store size={18} className="text-[--brand-primary] mt-0.5" />
                    <div className="text-sm">
                      <div className="font-semibold">Ritira qui il tuo ordine</div>
                      <div className="text-[--brand-muted] mt-0.5">
                        {settings?.address || 'Corso delle Province 169A, Catania'}
                      </div>
                      <div className="text-xs text-[--brand-primary] font-semibold mt-1">Costo di servizio: 0€</div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </Section>

            <Section title="Dati di contatto">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Nome <span className="text-[--brand-danger]">*</span></Label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Mario Rossi" className="rounded-xl" required data-testid="checkout-name-input" />
                </div>
                <div>
                  <Label>Telefono <span className="text-[--brand-danger]">*</span></Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+39 333 123456" type="tel" className="rounded-xl" required data-testid="checkout-phone-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Email <span className="text-[--brand-danger]">*</span></Label>
                  <Input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="tua@email.com"
                    className="rounded-xl"
                    required
                    aria-required="true"
                    data-testid="checkout-email-input"
                  />
                  <p className="text-xs text-[--brand-muted] mt-1">Ti servirà per ricevere la conferma d&apos;ordine.</p>
                </div>
              </div>
            </Section>

            {orderType === 'delivery' && (
            <Section title="Indirizzo di consegna">
              {savedAddresses.length > 0 && (
                <div className="mb-3">
                  <Label className="text-xs">Indirizzi salvati</Label>
                  <Select value={selectedAddrId || 'new'} onValueChange={(v) => {
                    if (v === 'new') {
                      setSelectedAddrId('');
                      setAddress('');
                      setQuote(null);
                      return;
                    }
                    const a = savedAddresses.find(x => x.id === v);
                    if (a) {
                      setSelectedAddrId(a.id);
                      setAddress(a.address);
                      setQuote({ lat: a.lat, lng: a.lng, display_name: a.address, deliverable: true, delivery_fee: null, zone_name: null });
                      if (a.notes) setNotes(a.notes);
                      api.post('/quote', { lat: a.lat, lng: a.lng }).then(qr => setQuote(qr.data)).catch(() => {});
                    }
                  }}>
                    <SelectTrigger className="rounded-xl" data-testid="checkout-saved-address-select">
                      <SelectValue placeholder="Scegli un indirizzo salvato..." />
                    </SelectTrigger>
                    <SelectContent>
                      {savedAddresses.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.label} — {a.address.split(',')[0]}{a.is_default ? ' ⭐' : ''}
                        </SelectItem>
                      ))}
                      <SelectItem value="new">➕ Nuovo indirizzo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex gap-2">
                <Input value={address} onChange={e => setAddress(e.target.value)}
                       onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
                       placeholder="Via Roma 12, Milano" className="rounded-xl" data-testid="checkout-address-input"/>
                <Button onClick={searchAddress} disabled={quoteLoading} className="rounded-xl bg-[--brand-primary]" data-testid="checkout-search-address-button">
                  {quoteLoading ? <Loader2 size={16} className="animate-spin"/> : <><Search size={16} className="mr-1"/>Cerca</>}
                </Button>
              </div>

              {quoteError && (
                <div className="mt-3 flex items-center gap-2 text-sm text-[--brand-danger]" data-testid="checkout-quote-error">
                  <AlertTriangle size={14}/> {quoteError}
                </div>
              )}

              {quote && (
                <div className="mt-3 space-y-2">
                  <div className="text-sm text-[--brand-muted]">{quote.display_name}</div>
                  <div className="h-[240px] sm:h-[300px]">
                    <BaseMap center={[quote.lat, quote.lng]} zoom={14} style={{ height: '100%' }}>
                      <Recenter position={[quote.lat, quote.lng]} />
                      {zones.map(z => (
                        <Polygon key={z.id} positions={z.polygon.coordinates[0].map(([lng, lat]) => [lat, lng])}
                                 pathOptions={{ color: z.color, weight: 2, fillOpacity: 0.12 }} />
                      ))}
                      {settings && (
                        <Marker position={[settings.lat, settings.lng]} icon={RESTAURANT_ICON} />
                      )}
                      <Marker position={[quote.lat, quote.lng]} icon={CUSTOMER_ICON} />
                    </BaseMap>
                  </div>
                  {quote.deliverable ? (
                    <div className="flex items-center justify-between rounded-xl bg-[#F3FBF7] border border-[--brand-primary]/30 px-4 py-3" data-testid="checkout-quote-success">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-[--brand-primary]"/>
                        <div>
                          <div className="font-semibold text-sm">Consegnabile</div>
                          <div className="text-xs text-[--brand-muted]">Zona: {quote.zone_name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[--brand-muted]">Consegna</div>
                        <div className="tabular-nums font-bold text-[--brand-primary]" data-testid="checkout-delivery-fee">{eur(quote.delivery_fee)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-[#FDECEC] text-[#9B1C1C] px-4 py-3 text-sm">
                      Ci dispiace, questo indirizzo è fuori dalla nostra area di consegna.
                    </div>
                  )}
                </div>
              )}
            </Section>
            )}

            <Section title={needsSchedule ? '⏰ Siamo chiusi — scegli slot di pre-ordine' : (orderType === 'takeaway' ? '⏰ Orario di ritiro' : '⏰ Consegna')}>
              {needsSchedule && (
                <div className="mb-3 rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-3 py-2 text-sm text-[#8A5A00]">
                  Siamo chiusi in questo momento. Riapriamo <b>{schedule?.next_open_display}</b>. Seleziona un orario di consegna per pre-ordinare.
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-[1fr,auto] items-start">
                {!needsSchedule && (
                  <label className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${scheduledFor === '' ? 'border-[--brand-primary] bg-[--brand-surface-2]' : 'border-[--brand-border] bg-white'}`}>
                    <input type="radio" checked={scheduledFor === ''} onChange={() => setScheduledFor('')} className="accent-[--brand-primary]"/>
                    <div>
                      <div className="font-semibold text-sm">{orderType === 'takeaway' ? 'Ritiro appena pronto' : 'Consegna appena possibile'}</div>
                      <div className="text-xs text-[--brand-muted]">{orderType === 'takeaway' ? 'Pronto in circa 15-25 min' : 'Stimato entro 45-60 min'}</div>
                    </div>
                  </label>
                )}
                <div className={`rounded-xl border p-3 ${scheduledFor ? 'border-[--brand-primary] bg-[--brand-surface-2]' : 'border-[--brand-border] bg-white'} ${needsSchedule ? 'sm:col-span-2' : ''}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {!needsSchedule && (
                      <input type="radio" checked={!!scheduledFor} onChange={() => setScheduledFor(availableSlots[0]?.iso || '')} className="accent-[--brand-primary]"/>
                    )}
                    <Calendar size={14}/>
                    <div className="font-semibold text-sm">{needsSchedule ? 'Slot di pre-ordine' : 'Scegli un orario preciso'}</div>
                  </div>
                  <Select value={scheduledFor || ''} onValueChange={(v) => setScheduledFor(v)}>
                    <SelectTrigger className="rounded-xl" data-testid="checkout-schedule-select">
                      <SelectValue placeholder="Seleziona uno slot..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {availableSlots.length === 0 && <div className="px-3 py-2 text-sm text-[--brand-muted]">Nessuno slot disponibile</div>}
                      {availableSlots.map(s => <SelectItem key={s.iso} value={s.iso}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Section>

            <Section title="Metodo di pagamento">
              <RadioGroup value={payment} onValueChange={setPayment} className="grid gap-2" data-testid="checkout-payment-method-radio">
                <label className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${payment === 'cash' ? 'border-[--brand-primary] bg-[#F3FBF7]' : 'border-[--brand-border] bg-white'}`}>
                  <RadioGroupItem value="cash" />
                  <div>
                    <div className="font-semibold">{orderType === 'takeaway' ? 'Contanti al ritiro' : 'Contanti alla consegna'}</div>
                    <div className="text-xs text-[--brand-muted]">Preparare l'importo esatto se possibile</div>
                  </div>
                </label>
                <label className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${payment === 'card' ? 'border-[--brand-primary] bg-[#F3FBF7]' : 'border-[--brand-border] bg-white'}`}>
                  <RadioGroupItem value="card" />
                  <div>
                    <div className="font-semibold">{orderType === 'takeaway' ? 'Carta al ritiro' : 'Carta alla consegna'}</div>
                    <div className="text-xs text-[--brand-muted]">{orderType === 'takeaway' ? 'POS disponibile in negozio' : 'Il rider ha il POS'}</div>
                  </div>
                </label>
              </RadioGroup>
            </Section>

            <Section title="Codice sconto (opzionale)">
              {discount ? (
                <div className="flex items-center justify-between rounded-xl bg-[#F3FBF7] border border-[--brand-primary]/30 px-4 py-3" data-testid="checkout-discount-applied">
                  <div className="flex items-center gap-2">
                    <Tag size={18} className="text-[--brand-primary]" />
                    <div>
                      <div className="font-semibold text-sm">Codice "{discount.code}" applicato</div>
                      <div className="text-xs text-[--brand-muted]">
                        {discount.type === 'percent' ? `${discount.value}% di sconto` : `€${Number(discount.value).toFixed(2)} di sconto`}
                        {' — '}Risparmio: <b className="text-[--brand-primary]">−{eur(discount.amount)}</b>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={removeDiscount} data-testid="checkout-discount-remove" className="text-[--brand-muted] hover:text-[--brand-danger]">
                    <X size={16}/>
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <Input
                      value={discountInput}
                      onChange={e => { setDiscountInput(e.target.value.toUpperCase()); setDiscountError(''); }}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), applyDiscount())}
                      placeholder="Inserisci codice (es. BENVENUTO10)"
                      className="rounded-xl uppercase"
                      data-testid="checkout-discount-input"
                    />
                    <Button onClick={applyDiscount} disabled={discountLoading || !discountInput.trim()} className="rounded-xl bg-[--brand-primary]" data-testid="checkout-discount-apply">
                      {discountLoading ? <Loader2 size={16} className="animate-spin"/> : <><Tag size={14} className="mr-1"/>Applica</>}
                    </Button>
                  </div>
                  {discountError && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-[--brand-danger]" data-testid="checkout-discount-error">
                      <AlertTriangle size={14}/> {discountError}
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title={orderType === 'takeaway' ? 'Note per il ritiro (opzionale)' : 'Note per la consegna (opzionale)'}>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                        placeholder={orderType === 'takeaway' ? 'Es. senza cipolla, allergie...' : "Es. citofono rotto, scala B, secondo piano..."}
                        className="rounded-xl" data-testid="checkout-delivery-notes"/>
            </Section>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-20 rounded-2xl bg-white border border-[--brand-border] p-5 shadow-[var(--shadow-sm)]">
              <h3 className="font-display text-lg font-bold">Riepilogo</h3>
              <div className="mt-3 space-y-2 text-sm">
                {items.map(it => (
                  <div key={it.id} className="flex justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-semibold">{it.quantity}× {it.name}</div>
                      {it.components_summary?.length > 0 && (
                        <div className="text-xs text-[--brand-muted] line-clamp-2">{it.components_summary[0]}</div>
                      )}
                    </div>
                    <div className="tabular-nums">{eur(it.unit_price * it.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[--brand-border] my-3"/>
              <Row label="Subtotale" value={eur(subtotal)} />
              <Row label={orderType === 'takeaway' ? 'Ritiro in negozio' : 'Consegna'} value={orderType === 'takeaway' ? 'Gratis' : (quote?.deliverable ? eur(quote.delivery_fee) : '—')} />
              {discount && (
                <div className="flex justify-between text-sm py-1" data-testid="checkout-discount-row">
                  <span className="text-[--brand-primary] font-semibold flex items-center gap-1"><Tag size={12}/> Sconto ({discount.code})</span>
                  <span className="tabular-nums font-semibold text-[--brand-primary]">−{eur(discount.amount)}</span>
                </div>
              )}
              <div className="border-t border-[--brand-border] my-3"/>
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-lg">Totale</span>
                <span className="font-display font-bold text-2xl tabular-nums text-[--brand-primary]" data-testid="checkout-total">{eur(total)}</span>
              </div>
              <Button className="w-full mt-4 rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] text-white"
                      disabled={submitting || (orderType === 'delivery' && !quote?.deliverable) || (needsSchedule && !scheduledFor)}
                      onClick={placeOrder}
                      data-testid="checkout-place-order-button">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2"/> : null}
                {scheduledFor ? 'Conferma pre-ordine' : (orderType === 'takeaway' ? 'Conferma ritiro' : 'Invia ordine')}
              </Button>
              {scheduledFor && (
                <div className="mt-3 text-xs text-center rounded-lg bg-[#FFF6E1] border border-[#F4D89A] text-[#8A5A00] px-3 py-2">
                  ⏰ Consegna schedulata: <b>{availableSlots.find(s => s.iso === scheduledFor)?.label}</b>
                </div>
              )}
              {!user && (
                <p className="text-xs text-[--brand-muted] mt-3 text-center">
                  Vuoi tenere traccia dei tuoi ordini? <a href="/login" className="text-[--brand-primary] font-semibold hover:underline">Accedi</a>
                </p>
              )}
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-[--brand-border] p-5 shadow-[var(--shadow-sm)]">
      <h3 className="font-display text-lg font-bold mb-3">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-[--brand-muted]">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
